
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import ActionSheet from '@/components/shared/ActionSheet.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import ModalWrapper from '@/components/shared/ModalWrapper.vue';
import PromptEditorModal from '@/components/rally-intelligence/PromptEditorModal.vue';
import PromptResultsModal from '@/components/catering-bot/PromptResultsModal.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import Toast from '@/utils/toast';
import { toErrorMessage } from '@/utils/errorHandling';
import TryPromptModal from '@/components/catering-bot/TryPromptModal.vue';

interface TryResultsPayload {
	numberOfPeople: number;
	totalBudget: number;
	packages: CateringPackage[];
	dietaryPreferences: string[];
	allergens: string[];
}

const namespace: string = 'rallyIntelligence';

@Component<PromptsList>({
	components: {
		ActionSheet,
		IconButton,
		ModalWrapper,
		PromptEditorModal,
		PromptResultsModal,
		StandardModal,
		TextButton,
		TextField,
		TryPromptModal
	}
})
export default class PromptsList extends Vue {
	@Getter('getPrompts', { namespace }) private prompts!: RallyIntelligencePromptResponse[];
	@Getter('getFetchPromptsComplete', { namespace }) private fetchPromptsComplete!: boolean;

	@Action('fetchPrompts', { namespace }) private fetchPrompts!: (search?: string) => Promise<void>;
	@Action('createPrompt', { namespace }) private createPrompt!: (draft: RallyIntelligencePromptRequest) => Promise<RallyIntelligencePromptResponse | void>;
	@Action('updatePrompt', { namespace }) private updatePrompt!: (payload: { id: number; draft: RallyIntelligencePromptRequest }) => Promise<RallyIntelligencePromptResponse | void>;
	@Action('deletePrompt', { namespace }) private deletePrompt!: (id: number) => Promise<void>;
	@Action('duplicatePrompt', { namespace }) private duplicatePrompt!: (id: number) => Promise<RallyIntelligencePromptResponse | void>;
	@Action('activatePrompt', { namespace }) private activatePrompt!: (id: number) => Promise<void>;
	@Action('fetchUsage', { namespace }) private fetchUsage!: () => Promise<void>;

	private searchInput: string = '';
	private showEditor: boolean = false;
	private saving: boolean = false;
	private showTry: boolean = false;
	private showResults: boolean = false;
	private tryPromptId: number | null = null;
	private editingPrompt: RallyIntelligencePromptResponse | null = null;
	private actionSheetPrompt: RallyIntelligencePromptResponse | null = null;
	private promptToDelete: RallyIntelligencePromptResponse | null = null;
	private selectedLocale: RestaurantLocale | null = null;
	private tryLocale: RestaurantLocale | null = null;
	private tryResults: TryResultsPayload | null = null;

	private get filteredPrompts(): RallyIntelligencePromptResponse[] {
		const query = this.searchInput.trim().toLowerCase();
		if (!query) {
			return this.prompts;
		}
		return this.prompts.filter(p => p.name.toLowerCase().includes(query) || (p.description ?? '').toLowerCase().includes(query));
	}

	private get actions(): ActionItem[] {
		const items: ActionItem[] = [
			{
				iconName: 'mdi-pencil-outline',
				buttonLabel: this.$t('rally-intelligence.prompts.actions.edit'),
				onClick: (_evt: unknown, prompt: RallyIntelligencePromptResponse) => {
					this.actionSheetPrompt = null;
					this.openEditor(prompt);
				}
			},
			{
				iconName: 'mdi-test-tube',
				buttonLabel: this.$t('rally-intelligence.prompts.actions.try'),
				onClick: (_evt: unknown, prompt: RallyIntelligencePromptResponse) => {
					this.actionSheetPrompt = null;
					this.tryPromptId = prompt.id;
					this.showTry = true;
				}
			},
			{
				iconName: 'mdi-content-copy',
				buttonLabel: this.$t('rally-intelligence.prompts.actions.duplicate'),
				onClick: (_evt: unknown, prompt: RallyIntelligencePromptResponse) => this.handleDuplicate(prompt)
			}
		];

		// Activate is only offered when the prompt isn't already active; insert it before Duplicate.
		if (!this.actionSheetPrompt?.is_active) {
			items.splice(2, 0, {
				iconName: 'mdi-check-circle-outline',
				buttonLabel: this.$t('rally-intelligence.prompts.actions.activate'),
				onClick: (_evt: unknown, prompt: RallyIntelligencePromptResponse) => this.handleActivate(prompt)
			});
		}

		// Global prompts can't be deleted, so Delete is only added for non-global ones.
		if (!this.actionSheetPrompt?.is_global) {
			items.push({
				iconName: 'mdi-trash-can-outline',
				buttonLabel: this.$t('rally-intelligence.prompts.actions.delete'),
				onClick: (_evt: unknown, prompt: RallyIntelligencePromptResponse) => this.handleDelete(prompt)
			});
		}

		return items;
	}

	private async mounted(): Promise<void> {
		try {
			await this.fetchPrompts();
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.prompts.error_fetching', { errorMessage: toErrorMessage(error) }));
		}
		// Best-effort refresh of the tokens-remaining counter — a failed refresh
		// just leaves the stale count, so there's no toast on error.
		this.fetchUsage().catch(() => {});
	}

	private openEditor(prompt: RallyIntelligencePromptResponse | null = null): void {
		this.editingPrompt = prompt;
		this.showEditor = true;
	}

	private closeEditor(): void {
		this.showEditor = false;
		this.editingPrompt = null;
		this.selectedLocale = null;
	}

	private async handleSaveAndTest(draft: RallyIntelligencePromptRequest): Promise<void> {
		const saved = await this.persistDraft(draft);
		if (saved) {
			this.tryPromptId = saved.id;
			this.showTry = true;
		}
	}

	private async persistDraft(draft: RallyIntelligencePromptRequest): Promise<RallyIntelligencePromptResponse | void> {
		this.saving = true;
		try {
			const saved = this.editingPrompt
				? await this.updatePrompt({ id: this.editingPrompt.id, draft })
				: await this.createPrompt(draft);
			Toast.success(this.$t(this.editingPrompt ? 'rally-intelligence.prompts.toast_updated' : 'rally-intelligence.prompts.toast_created'));
			this.closeEditor();
			return saved;
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.prompts.error_saving', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.saving = false;
		}
	}

	private handleEditFromTry(): void {
		const prompt = this.prompts.find(p => p.id === this.tryPromptId);
		this.closeTry();
		if (prompt) {
			this.openEditor(prompt);
		}
	}

	private closeTry(): void {
		this.showTry = false;
		this.tryLocale = null;
	}

	private handleTryResults(payload: TryResultsPayload): void {
		this.tryResults = payload;
		this.showTry = false;
		this.showResults = true;
	}

	private handleResultsBack(): void {
		this.showResults = false;
		this.tryLocale = null;
		this.showTry = true;
	}

	private closeResults(): void {
		this.showResults = false;
		this.tryResults = null;
		this.tryPromptId = null;
		this.tryLocale = null;
	}

	private async handleDuplicate(prompt: RallyIntelligencePromptResponse): Promise<void> {
		this.actionSheetPrompt = null;
		try {
			await this.duplicatePrompt(prompt.id);
			Toast.success(this.$t('rally-intelligence.prompts.toast_duplicated'));
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.prompts.error_duplicating', { errorMessage: toErrorMessage(error) }));
		}
	}

	private async handleActivate(prompt: RallyIntelligencePromptResponse): Promise<void> {
		this.actionSheetPrompt = null;
		if (prompt.is_active) {
			return;
		}
		try {
			await this.activatePrompt(prompt.id);
			Toast.success(this.$t('rally-intelligence.prompts.toast_activated'));
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.prompts.error_activating', { errorMessage: toErrorMessage(error) }));
		}
	}

	private handleDelete(prompt: RallyIntelligencePromptResponse): void {
		this.actionSheetPrompt = null;
		this.promptToDelete = prompt;
	}

	private async confirmDelete(): Promise<void> {
		if (!this.promptToDelete) {
			return;
		}
		const id = this.promptToDelete.id;
		this.promptToDelete = null;
		try {
			await this.deletePrompt(id);
			Toast.success(this.$t('rally-intelligence.prompts.toast_deleted'));
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.prompts.error_deleting', { errorMessage: toErrorMessage(error) }));
		}
	}
}
